import { z } from 'zod';
import { type ComputedSection, computeSection, type ResultInput } from './compute';
import type { PatientContext } from './ranges';
import { Sex, TemplateDoc } from './template';

/** Who ran a section of a report. Outsourced sections print the reference lab. */
export const PerformedByDoc = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('in_house') }),
  z.object({
    kind: z.literal('reference_lab'),
    /** May be empty while the lab is still typing; signing needs it (see summarizeReport). */
    name: z.string(),
    accreditation: z.string().optional(),
  }),
]);
export type PerformedByDoc = z.infer<typeof PerformedByDoc>;

/**
 * How a value outside its reference range prints. Red and bold is how reports have always looked;
 * a lab can choose differently for all its reports, and a single report or result can override it
 * (some patients do not want red on their report).
 */
export const ABNORMAL_COLORS = {
  black: { label: 'Black', hex: '#000000' },
  red: { label: 'Red', hex: '#c62828' },
  blue: { label: 'Blue', hex: '#1565c0' },
  // A dark yellow, so it can still be read on white paper.
  yellow: { label: 'Yellow', hex: '#b07d00' },
} as const;
export const AbnormalColor = z.enum(['black', 'red', 'blue', 'yellow']);
export type AbnormalColor = z.infer<typeof AbnormalColor>;
export const ABNORMAL_COLOR_ORDER: AbnormalColor[] = ['black', 'red', 'blue', 'yellow'];

/** A choice made for part of a report; what is left out follows the next level up. */
export const AbnormalStyle = z.object({
  bold: z.boolean().optional(),
  color: AbnormalColor.optional(),
});
export type AbnormalStyle = z.infer<typeof AbnormalStyle>;

/** What an abnormal value looks like when nobody has chosen anything. */
export const DEFAULT_ABNORMAL = { bold: true, color: 'red' } as const satisfies {
  bold: boolean;
  color: AbnormalColor;
};

/**
 * The look of an abnormal value: the result's own choice, else the report's, else the lab's, else
 * bold red. Each of bold and colour is decided on its own.
 */
export function resolveAbnormal(
  lab:
    | { abnormalBold?: boolean | undefined; abnormalColor?: AbnormalColor | undefined }
    | undefined,
  report: AbnormalStyle | undefined,
  row: AbnormalStyle | undefined,
): { bold: boolean; color: AbnormalColor; from: 'result' | 'report' | 'lab' | 'default' } {
  const bold = row?.bold ?? report?.bold ?? lab?.abnormalBold ?? DEFAULT_ABNORMAL.bold;
  const color = row?.color ?? report?.color ?? lab?.abnormalColor ?? DEFAULT_ABNORMAL.color;
  const from =
    row && (row.bold !== undefined || row.color !== undefined)
      ? 'result'
      : report && (report.bold !== undefined || report.color !== undefined)
        ? 'report'
        : lab && (lab.abnormalBold !== undefined || lab.abnormalColor !== undefined)
          ? 'lab'
          : 'default';
  return { bold, color, from };
}

export const ReportSection = z.object({
  /** Set for sections made from a saved template. */
  templateId: z.string().optional(),
  templateVersion: z.number().int().positive().optional(),
  /**
   * The template exactly as it was when the report was made. Reports carry their own copy, so
   * they print the same forever and work offline without looking templates up.
   */
  doc: TemplateDoc,
  /** Typed results by row id. Calculated rows are never stored. */
  values: z.record(z.string(), z.string()).default({}),
  narratives: z.record(z.string(), z.string()).optional(),
  /** How an out-of-range value of a single result prints, by row id, when it differs from the report's. */
  rowStyles: z.record(z.string(), AbnormalStyle).optional(),
  /** Answers to the template's extra fields (e.g. abstinence days), by key. */
  extra: z.record(z.string(), z.string()).optional(),
  performedBy: PerformedByDoc.optional(),
  verifiedBy: z.string().optional(),
  signedBy: z.string().optional(),
  signedAt: z.string().optional(),
});
export type ReportSection = z.infer<typeof ReportSection>;

export const PatientSnapshot = z.object({
  salutation: z.string().optional(),
  name: z.string().min(1),
  sex: Sex.optional(),
  ageText: z.string().default(''),
  ageDays: z.number().int().nonnegative().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  address: z.string().optional(),
  externalId: z.string().optional(),
});
export type PatientSnapshot = z.infer<typeof PatientSnapshot>;

/** One sample taken from the patient. A visit can have several: two bloods, a urine, and so on. */
export const Sample = z.object({
  id: z.string(),
  /** Blood, Urine, Semen, or anything else the lab types in. */
  type: z.string().min(1),
  /** When it was collected (ISO). */
  collectedAt: z.string().optional(),
  /** A barcode or the number on the tube or the machine, if the lab uses one. */
  identifier: z.string().optional(),
});
export type Sample = z.infer<typeof Sample>;

export const ReportContent = z.object({
  kind: z.enum(['templated', 'generic']).default('templated'),
  /** The report's own title; for generic reports this is "Title (report name)". */
  title: z.string().optional(),
  patient: PatientSnapshot,
  /** Extra metadata shown with the patient: referred by, sample type, custom fields… */
  fields: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  visit: z
    .object({
      visitNo: z.string().optional(),
      referredBy: z.string().optional(),
      sampleType: z.string().optional(),
      collectedAt: z.string().optional(),
      receivedAt: z.string().optional(),
      reportedAt: z.string().optional(),
      /** The lab typed the reported time in. Signing keeps it; otherwise signing sets it. */
      reportedAtManual: z.boolean().optional(),
    })
    .default({}),
  /** What was collected. The older single `visit.sampleType` still prints when there are none. */
  samples: z.array(Sample).default([]),
  sections: z.array(ReportSection),
  /** The lab's free comments under the results, in the light markers of `richtext.ts`. */
  comments: z.string().max(5000).optional(),
  /** How out-of-range values print in this report, when it differs from the lab's setting. */
  abnormal: AbnormalStyle.optional(),
  signoff: z.lazy(() => Signoff).optional(),
});
export type ReportContent = z.infer<typeof ReportContent>;
export type ReportContentInput = z.input<typeof ReportContent>;

/**
 * Everything on a printed report that the lab may switch off, for printing on pre-printed
 * stationery. The patient's details and the results always print.
 */
export const PRINT_ITEMS = [
  'logo',
  'labName',
  'address',
  'contact',
  'registrations',
  'headerLine',
  'qrCode',
  'signatureImage',
  'signatoryNames',
  'disclaimer',
  'reportLine',
  'pageNumbers',
  'footerLine',
] as const;
export type PrintItem = (typeof PRINT_ITEMS)[number];
export type PrintChoices = Record<PrintItem, boolean>;

/** The part of the page a print item belongs to. */
export const PRINT_GROUPS: { title: string; items: { id: PrintItem; label: string }[] }[] = [
  {
    title: 'Header',
    items: [
      { id: 'logo', label: 'Logo' },
      { id: 'labName', label: 'Lab name and tagline' },
      { id: 'address', label: 'Address' },
      { id: 'contact', label: 'Phone numbers and email' },
      { id: 'registrations', label: 'Registration numbers' },
      { id: 'headerLine', label: 'Coloured line under the header' },
    ],
  },
  { title: 'Patient details', items: [{ id: 'qrCode', label: 'QR code to verify the report' }] },
  {
    title: 'Signatures',
    items: [
      { id: 'signatureImage', label: 'Signature image' },
      { id: 'signatoryNames', label: 'Name, qualification and registration number' },
    ],
  },
  {
    title: 'Footer',
    items: [
      { id: 'disclaimer', label: 'Disclaimer' },
      { id: 'reportLine', label: 'Report number and patient name' },
      { id: 'pageNumbers', label: 'Page numbers' },
      { id: 'footerLine', label: 'Line above the footer' },
    ],
  },
];

const HEADER_ITEMS: PrintItem[] = [
  'logo',
  'labName',
  'address',
  'contact',
  'registrations',
  'headerLine',
];

export const BrandSettings = z.object({
  labName: z.string().default(''),
  tagline: z.string().optional(),
  /** Where the lab name sits on the letterhead, relative to the logo. */
  nameAt: z.enum(['above', 'below', 'left', 'right', 'hidden']).default('right'),
  address: z.string().default(''),
  phones: z.string().default(''),
  email: z.string().optional(),
  website: z.string().optional(),
  accent: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#2b2157'),
  logoFileId: z.string().optional(),
  /** How a value outside its reference range prints, for all of the lab's reports. Bold and red when unset. */
  abnormalBold: z.boolean().optional(),
  abnormalColor: AbnormalColor.optional(),
  /**
   * A full-page picture printed behind every page: the lab's own letterhead (logo, address and
   * contact lines, a faint watermark). The report is then printed in the space the picture leaves,
   * using the top and bottom margins below.
   */
  backgroundFileId: z.string().optional(),
  registrations: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  disclaimer: z.string().optional(),
  /** Older setting: "I print on pre-printed letterhead". Still read for labs that saved it. */
  letterhead: z.boolean().default(false),
  /** Print everything. When on, the choices in `print` are ignored. Unset: follows `letterhead`. */
  printAll: z.boolean().optional(),
  /** What to print when `printAll` is off. An item that is not listed prints. */
  print: z.record(z.string(), z.boolean()).optional(),
  /** Letters that start every new lab number, e.g. SUN gives SUN-2026-000001. Empty: none. */
  labNumberPrefix: z
    .string()
    .regex(/^[A-Za-z0-9]{0,6}$/)
    .optional(),
  /**
   * Print a small "LabView by Aharbal" contact line at the foot of each page. On by default for a
   * lab that has never chosen; a report signed before this setting existed keeps no line.
   * Left unset in a saved copy it means "not chosen": see `labviewFooterOn`.
   */
  labviewFooter: z.boolean().optional(),
  /**
   * Make the patient get a code on their WhatsApp or email before the report opens from its QR or
   * verification code. Off unless the lab turns it on: a link sent to the patient opens directly.
   * Reports marked sensitive always ask for the code.
   */
  requirePatientCode: z.boolean().optional(),
  /** How tall the space for each signature is, in mm (the picture is fitted into it). */
  signatureHeightMm: z.number().min(6).max(30).default(9),
  /** How wide each signature block is, in mm. Three fit across an A4 page. */
  signatureWidthMm: z.number().min(40).max(60).default(56),
  topMarginMm: z.number().min(10).max(120).default(64),
  bottomMarginMm: z.number().min(10).max(80).default(38),
  signaturesOn: z.enum(['every-page', 'last-page']).default('every-page'),
});
export type BrandSettings = z.infer<typeof BrandSettings>;

/** Who makes LabView, as printed in the report footer and at the foot of emails. */
export const LABVIEW_CONTACT = {
  name: 'LabView by Aharbal',
  email: 'labview@aharbal.com',
  phone: '+91 6006883845',
} as const;

/** The footer line itself. */
export const LABVIEW_CONTACT_LINE = `${LABVIEW_CONTACT.name} · ${LABVIEW_CONTACT.email} · ${LABVIEW_CONTACT.phone}`;

/** Whether this saved copy of the lab's settings prints the line (only when it says so). */
export const labviewFooterOn = (brand: Pick<BrandSettings, 'labviewFooter'>): boolean =>
  brand.labviewFooter === true;

export const DEFAULT_BRAND: BrandSettings = BrandSettings.parse({});

/** Is the lab printing everything (the usual case), rather than choosing item by item? */
export function printsEverything(brand: Pick<BrandSettings, 'letterhead' | 'printAll'>): boolean {
  return brand.printAll ?? !brand.letterhead;
}

/**
 * What to print, item by item. A lab that saved the older "pre-printed letterhead" setting keeps
 * what it had: the header is left out and everything else prints.
 */
export function effectivePrint(
  brand: Pick<BrandSettings, 'letterhead' | 'printAll' | 'print'>,
): PrintChoices {
  const all = printsEverything(brand);
  const out = {} as PrintChoices;
  for (const id of PRINT_ITEMS) {
    if (all) out[id] = true;
    else if (brand.print && id in brand.print) out[id] = brand.print[id] !== false;
    else
      out[id] =
        brand.printAll === undefined && brand.letterhead ? !HEADER_ITEMS.includes(id) : true;
  }
  return out;
}

/**
 * Does this lab's report carry a doctor's signature (the image or the name)? Only then does a
 * report need a signing doctor on file. A lab that has switched signatures off can sign reports
 * without one.
 */
export function printsSignatures(
  brand: Pick<BrandSettings, 'letterhead' | 'printAll' | 'print'>,
): boolean {
  const p = effectivePrint(brand);
  return p.signatureImage || p.signatoryNames;
}

/** The choices to start from when a lab stops printing everything: header off, the rest on. */
/**
 * What to change when a lab puts its letterhead picture behind the report: its own header is not
 * printed (the picture has it), and the top and bottom margins leave room for the picture's
 * artwork. `fit` is how tall that artwork is at the top and at the foot, in mm.
 */
export function backgroundSettings(
  current: BrandSettings,
  fit: { headerMm: number; footerMm: number } | null,
): Partial<BrandSettings> {
  const choices = { ...effectivePrint(current) };
  for (const id of HEADER_ITEMS) choices[id] = false;
  return {
    letterhead: true,
    printAll: false,
    print: choices,
    // Under the artwork: the patient's details repeat on every page and need about 30 mm.
    ...(fit
      ? {
          topMarginMm: Math.min(120, Math.max(10, Math.round(fit.headerMm + 6 + 30))),
          bottomMarginMm: Math.min(80, Math.max(10, Math.round(fit.footerMm + 4 + 34))),
        }
      : {}),
  };
}

export function letterheadPreset(): PrintChoices {
  const out = {} as PrintChoices;
  for (const id of PRINT_ITEMS) out[id] = !HEADER_ITEMS.includes(id);
  return out;
}

/**
 * How the report looked when it was signed: the lab's branding and the signatories. Kept with
 * the signed version so it prints the same forever, even if the lab later changes its logo,
 * address or a doctor's details.
 */
export const Signoff = z.object({
  brand: BrandSettings,
  signatories: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      qualification: z.string(),
      designation: z.string().nullish(),
      regNo: z.string().nullish(),
      signatureFileId: z.string().nullish(),
    }),
  ),
});
export type Signoff = z.infer<typeof Signoff>;

/** Compute every section of a report for printing and review. */
export function computeReportSections(content: ReportContent): ComputedSection[] {
  const ctx: PatientContext = {
    ...(content.patient.sex ? { sex: content.patient.sex } : {}),
    ...(content.patient.ageDays !== undefined ? { ageDays: content.patient.ageDays } : {}),
  };
  return content.sections.map((s) => {
    const input: ResultInput = s.values;
    return computeSection(s.doc, input, ctx, {
      ...(s.narratives ? { narratives: s.narratives } : {}),
    });
  });
}

export interface ReportSummary {
  /** Every value outside its range, in report order. */
  flagged: { label: string; value: string; flag: string; band?: string }[];
  critical: number;
  /** Problems that must be fixed before the report can be signed. */
  blocking: string[];
  testCount: number;
  filledCount: number;
}

export function summarizeReport(content: ReportContent): ReportSummary {
  const computed = computeReportSections(content);
  const summary: ReportSummary = {
    flagged: [],
    critical: 0,
    blocking: [],
    testCount: 0,
    filledCount: 0,
  };
  for (const c of computed) {
    for (const r of c.rows) {
      if (r.kind !== 'test') continue;
      summary.testCount++;
      if (r.display !== '') summary.filledCount++;
      if (r.flag.code) {
        summary.flagged.push({
          label: r.row.label,
          value: `${r.display}${r.row.unit ? ` ${r.row.unit}` : ''}`,
          flag: r.flag.code,
          ...(r.flag.band ? { band: r.flag.band } : {}),
        });
      }
      if (r.flag.critical) summary.critical++;
    }
    for (const b of c.blocking) summary.blocking.push(b.issue.message);
  }
  // A test with no result at all would print as an empty table.
  content.sections.forEach((sec, i) => {
    const tests = computed[i]?.rows.filter((r) => r.kind === 'test') ?? [];
    if (tests.length > 0 && tests.every((r) => r.kind === 'test' && r.display === '')) {
      summary.blocking.push(`${sec.doc.name} has no results yet`);
    }
    if (sec.performedBy?.kind === 'reference_lab' && !sec.performedBy.name.trim()) {
      summary.blocking.push(`Enter the name of the reference lab for ${sec.doc.name}`);
    }
  });
  return summary;
}

/** Sensitive when any section came from a sensitive template. */
export function isSensitive(content: ReportContent): boolean {
  return content.sections.some((s) => s.doc.sensitive);
}

/** Text searched when finding a report: name, patient, tests. */
export function reportSearchText(name: string, labNo: string, content: ReportContent): string {
  const tests = content.sections.map((s) => s.doc.name);
  return [
    name,
    labNo,
    content.patient.name,
    content.patient.phone ?? '',
    content.patient.externalId ?? '',
    ...tests,
  ]
    .join(' ')
    .toLowerCase();
}
